/**
 * Remembers that a dial admin chose "Got it" on a dial's update notice (KEY-583).
 *
 * The server keeps returning a Pending/Accepted upgrade until it is applied (correct), so without a
 * memory the notice came back on every page load. Dismissal is per-browser, one value per dial, keyed
 * by a fingerprint of the notice: a new upgrade, an accepted status, a moved date or a status-mapping
 * change all read as a different notice and show again.
 *
 * Every storage access is wrapped: a blocked or full store must never take the workspace down.
 */
import type { PendingUpgradeView } from "@keydials/api-client";

export const UPGRADE_NOTICE_DISMISSED_KEY_PREFIX = "kde.upgrade-notice-dismissed.v1.";

/** A mandatory upgrade that deletes data resurfaces this close to its date, whatever was dismissed. */
export const MANDATORY_RESURFACE_MS = 3 * 24 * 60 * 60 * 1000;

type NoticeStorage = Pick<Storage, "getItem" | "setItem">;

const keyFor = (instanceId: string) => `${UPGRADE_NOTICE_DISMISSED_KEY_PREFIX}${instanceId}`;

export function noticeFingerprint(view: PendingUpgradeView): string {
  // The structure flag (KEY-806) is appended only when set, so a notice dismissed before it existed stays dismissed.
  return `${view.id}|${view.status}|${view.effectiveDateUtc}|${view.needsStatusMapping ? 1 : 0}${view.needsStructureMapping ? "|s" : ""}`;
}

function defaultStorage(): NoticeStorage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

export function isNoticeDismissed(
  instanceId: string,
  view: PendingUpgradeView,
  storage: NoticeStorage | null = defaultStorage(),
  now: number = Date.now(),
): boolean {
  try {
    if (view.kind === "Mandatory") {
      const effective = new Date(view.effectiveDateUtc).getTime();
      if (Number.isFinite(effective) && effective - now < MANDATORY_RESURFACE_MS) return false;
    }
    return storage?.getItem(keyFor(instanceId)) === noticeFingerprint(view);
  } catch {
    return false;
  }
}

export function dismissNotice(
  instanceId: string,
  view: PendingUpgradeView,
  storage: NoticeStorage | null = defaultStorage(),
): void {
  try {
    storage?.setItem(keyFor(instanceId), noticeFingerprint(view));
  } catch {
    // Storage full, blocked, or disabled. The notice returns next load; a crash would be worse.
  }
}
